import { useEffect, useMemo, useState, type FunctionComponent } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Card, DatePicker, Divider, Input, Space, Table, Tag, Tooltip, type TableProps } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import {
  productOrdersActions,
  productOrdersSelectors,
  RequestStatus,
  type OrderWithRelations,
} from "@fitness-app/app-store";
import { OrderStatusOption, type Order, type OrderItem } from "@fitness-app/data-models/entities/Order";
import { PaymentTypeOption } from "@fitness-app/data-models/entities/Product";
import { getHumanPrice } from "@fitness-app/utils/src/products/getHumanPrice";

import InvoiceDataInfo from "~/modules/Products/components/InvoiceDataInfo/InvoiceDataInfo";
import InvoiceNumberInfo from "~/modules/Products/components/InvoiceNumberInfo/InvoiceNumberInfo";
import {
  applyOrderFilters,
  EMPTY_FILTER_VALUE,
  INVOICE_ERROR_FILTER,
  type OrderTableFilters,
} from "~/modules/Products/Product/ProductOrders/applyOrderFilters";
import { exportOrdersToCsv } from "~/modules/Products/Product/ProductOrders/exportOrdersToCsv";
import IssueInvoice from "~/modules/Products/Product/ProductOrders/IssueInvoice";
import { OrderPaidAtCell } from "~/modules/Products/helpers/OrderPaidAtCell";
import { OrderStatusCell } from "~/modules/Products/helpers/OrderStatusCell";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { CrossSellInfo } from "../../components/CrossSellInfo/CrossSellInfo";
import { OrderPaymentCell } from "../../helpers/OrderPaymentCell";
import { toOrderPaymentDescriptor } from "../../helpers/describeOrderPayment";

interface OwnProps {
  orders?: Order[] | null;
  productId: string;
  ownerId?: string;
}

type Props = OwnProps;

function disabledDate(current: Dayjs) {
  return current && current > dayjs().endOf("month");
}

export { INVOICE_ERROR_FILTER };

const ProductOrders: FunctionComponent<Props> = ({ productId, ownerId }) => {
  const { t } = useTranslation(["products", "common", "client"]);
  const [selectedMonth, setMonth] = useState<Dayjs | null>(dayjs());
  const [searchValue, setSearchValue] = useState("");
  const [tableFilters, setTableFilters] = useState<OrderTableFilters>({});
  const orders = useSelector(productOrdersSelectors.getFilteredOrders);
  const dispatch = useAppDispatch();
  const { ordersStatus, searchTerm } = useAppSelector((store) => store.productOrders);

  const visibleOrders = useMemo(() => applyOrderFilters(orders, tableFilters), [orders, tableFilters]);

  const variantFilters = useMemo(() => {
    const seen = new Map<string, string>();
    let hasEmpty = false;

    for (const order of orders || []) {
      if (!order.checkoutVariantSlug) {
        hasEmpty = true;
      } else if (!seen.has(order.checkoutVariantSlug)) {
        seen.set(order.checkoutVariantSlug, order.checkoutVariantSlug);
      }
    }

    const filters = Array.from(seen.entries()).map(([slug]) => ({ text: slug, value: slug }));
    if (hasEmpty) {
      filters.unshift({ text: "Brak", value: EMPTY_FILTER_VALUE });
    }
    return filters;
  }, [orders]);

  const funnelFilters = useMemo(() => {
    const seen = new Map<string, string>();
    let hasEmpty = false;

    for (const order of orders || []) {
      if (!order.salesFunnelName) {
        hasEmpty = true;
      } else if (!seen.has(order.salesFunnelName)) {
        seen.set(order.salesFunnelName, order.salesFunnelName);
      }
    }

    const filters = Array.from(seen.entries()).map(([name]) => ({ text: name, value: name }));
    if (hasEmpty) {
      filters.unshift({ text: "Brak", value: EMPTY_FILTER_VALUE });
    }
    return filters;
  }, [orders]);

  useEffect(() => {
    if (selectedMonth) {
      void dispatch(productOrdersActions.fetchOrders({ productId, selectedMonth, ownerId }));
    }
  }, [selectedMonth, productId, dispatch, ownerId]);

  const onClear = () => {
    setSearchValue("");
    dispatch(productOrdersActions.clearSearchMode());
  };

  const onSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      if (searchTerm) {
        onClear();
      }
      return;
    }
    dispatch(productOrdersActions.setSearchTerm(trimmed));
  };

  const onTableChange: TableProps<Order>["onChange"] = (_pagination, filters) => {
    setTableFilters(filters as OrderTableFilters);
  };

  return (
    <Card
      title={
        <Space size={16}>
          <span>
            {t("orders.selectMonth", {
              month: selectedMonth ? t(`common:month.${selectedMonth.month() + 1}`) : "-",
            })}
          </span>
          <DatePicker
            picker="month"
            onChange={setMonth}
            style={{ minWidth: 200 }}
            allowClear={false}
            disabledDate={disabledDate}
            value={selectedMonth}
          />
        </Space>
      }
      extra={
        <Button
          type="dashed"
          onClick={() => exportOrdersToCsv(visibleOrders, productId, selectedMonth?.format("MM-YYYY") ?? "none")}
        >
          {t("orders.exportToCsv")}
        </Button>
      }
    >
      {/*<ProductMonthStatistics />*/}
      <Divider />
      <Space direction="vertical" style={{ width: "100%" }} size={24}>
        <div style={{ width: "100%", display: "flex", alignItems: "center" }}>
          <Input.Search
            placeholder={t<string>("search.orderPlaceholder")}
            onSearch={onSearch}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ width: 400, margin: "20px 0" }}
          />
          {Boolean(searchTerm) && (
            <Button onClick={onClear} type="link">
              {t("search.reset")}
            </Button>
          )}
        </div>

        <Table<Order>
          loading={ordersStatus === RequestStatus.SUBSCRIBING}
          rowKey="id"
          dataSource={visibleOrders}
          pagination={false}
          scroll={{ x: true }}
          onChange={onTableChange}
          expandedRowRender={(order) => <InvoiceDataInfo orderClient={order.client} />}
          summary={(currentData) => {
            const totalsByCurrency: Record<string, number> = {};
            for (const order of currentData) {
              if (order.price && !order.price.free) {
                const currency = order.price.currency || "PLN";
                totalsByCurrency[currency] = (totalsByCurrency[currency] || 0) + order.price.amount;
              }
            }

            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>
                  <b>Razem: {currentData.length}</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6}>
                  <b className="whitespace-nowrap">
                    {Object.entries(totalsByCurrency)
                      .map(([currency, amount]) => getHumanPrice(amount, currency))
                      .join(" / ") || "-"}
                  </b>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} colSpan={7} />
              </Table.Summary.Row>
            );
          }}
        >
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.index")}
            width={50}
            dataIndex="index"
            key="index"
            render={(name, row, i) => i + 1}
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.orderNumber")}
            width={50}
            dataIndex="orderNumber"
            key="orderNumber"
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.name")}
            dataIndex="name"
            key="name"
            render={(_, row) =>
              row.client.lastName || row.client.firstName
                ? `${row.client.lastName || ""} ${row.client.firstName || ""}`.trim()
                : "-"
            }
            sorter={(a, b) => a.client.lastName?.localeCompare(b.client.lastName)}
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.email")}
            dataIndex="clientEmail"
            key="clientEmail"
            sorter={(a, b) => a.clientEmail.length - b.clientEmail.length}
            render={(email) => <Link to={`/products/${productId}/clients/${email}`}>{email}</Link>}
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.createdAt")}
            dataIndex="createdAt"
            key="createdAt"
            render={(createdAt: string) => dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
            defaultSortOrder="descend"
            sorter={(a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix()}
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.price")}
            dataIndex="price"
            key="price"
            render={(price: OrderItem, order) =>
              price ? (
                <Space>
                  <b className="whitespace-nowrap">{getHumanPrice(price.amount, price.currency)}</b>
                  <CrossSellInfo crossSell={order.crossSell} />
                </Space>
              ) : (
                "-"
              )
            }
            sorter={(a, b) =>
              (!a.price || a.price.free ? 0 : a.price.amount) - (!b.price || b.price.free ? 0 : b.price.amount)
            }
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.status")}
            dataIndex="status"
            key="status"
            filteredValue={tableFilters.status ?? null}
            filters={Object.values(OrderStatusOption).map((status) => ({
              value: status,
              text: t(`productTransactions.type.${status.toLowerCase()}`),
            }))}
            render={(_type, record) => <OrderStatusCell order={record} />}
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.paidAt")}
            dataIndex="paidAt"
            key="paidAt"
            render={(_value, record) => <OrderPaidAtCell order={record} />}
          />
          <Table.Column<Order>
            title={t("productClients.tableHeader.paymentType")}
            dataIndex="paymentType"
            key="paymentType"
            render={(type, record) => <Tag>{t(`client:type.${record.installments ? "installments" : type}`)}</Tag>}
            filteredValue={tableFilters.paymentType ?? null}
            filters={[...Object.values(PaymentTypeOption), "installments"].map((type) => ({
              value: type,
              text: t(`client:type.${type}`),
            }))}
          />

          <Table.Column<Order>
            title={t("productClients.tableHeader.orderType")}
            dataIndex="orderType"
            key="orderType"
            render={(_type, record) => {
              if (record.paymentCycle && record.paymentCycle > 1) {
                return `Kolejny cykl płatności (${record.paymentCycle})`;
              }

              if (record.metadata?.renewal) {
                return (
                  <Tooltip title={record.metadata.sessionMetadata?.variantSlug} className="whitespace-nowrap">
                    Odnowienie dostępu {record.metadata?.sessionMetadata?.variantSlug ? <InfoCircleOutlined /> : null}
                  </Tooltip>
                );
              }

              return (
                <Tooltip title={record.metadata?.sessionMetadata?.variantSlug} className="whitespace-nowrap">
                  Nowy zakup {record.metadata?.sessionMetadata?.variantSlug ? <InfoCircleOutlined /> : null}
                </Tooltip>
              );
            }}
            filteredValue={tableFilters.orderType ?? null}
            filters={[
              {
                text: "Nowy zakup",
                value: "new",
              },
              {
                text: "Odnowienie dostępu",
                value: "renewal",
              },
              {
                text: "Kolejny cykl płatności",
                value: "nextCycle",
              },
            ]}
          />

          <Table.Column<Order>
            title={t("productTransactions.tableHeader.paymentGateway")}
            dataIndex="paymentGateway"
            key="paymentGateway"
            render={(_paymentGateway: string, record) => (
              <OrderPaymentCell order={toOrderPaymentDescriptor(record)} />
            )}
          />
          <Table.Column<OrderWithRelations>
            title="Wariant"
            dataIndex="checkoutVariantSlug"
            key="checkoutVariantSlug"
            render={(slug: string | null) => slug || "-"}
            filteredValue={tableFilters.checkoutVariantSlug ?? null}
            filters={variantFilters}
          />
          <Table.Column<OrderWithRelations>
            title="Lejek sprzedażowy"
            dataIndex="salesFunnelName"
            key="salesFunnelName"
            render={(name: string | null) => name || "-"}
            filteredValue={tableFilters.salesFunnelName ?? null}
            filters={funnelFilters}
          />
          <Table.Column<Order>
            title={t("productTransactions.tableHeader.invoice")}
            dataIndex="connectedInvoice"
            key="connectedInvoice"
            render={(connectedInvoice: Order["connectedInvoice"], order) => (
              <div className="flex w-full items-center justify-between">
                <InvoiceNumberInfo
                  invoice={connectedInvoice}
                  withId
                  orderId={order.id}
                  productId={order.product}
                />
                {selectedMonth && <IssueInvoice order={order} selectedMonth={selectedMonth} />}
              </div>
            )}
            filteredValue={tableFilters.connectedInvoice ?? null}
            filters={[
              {
                text: t("common:yes"),
                value: true,
              },
              { text: t("common:no"), value: false },
              { text: t("common:errorInvoices"), value: INVOICE_ERROR_FILTER },
            ]}
          />
        </Table>
      </Space>
    </Card>
  );
};

export default ProductOrders;
